import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { FaExpandArrowsAlt } from 'react-icons/fa';

import { Responsive, WidthProvider } from 'react-grid-layout';
// import Toggle from 'react-toggle';
import userStorage from '@/shared/services/userStorage';
import intlShape from '@/shared/utils/intlShape';
import DataCacheService from '@/shared/services/DataCacheService';
import Button from '@/shared/components/Button';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import {
  addItemToLayouts,
  breakpointCols,
  breakpoints,
  defaultLayouts,
  getEnabledDashboardDefinitions,
  removeItemFromLayouts,
  validateLayouts,
} from './dashboardHelpers';
import layoutService from './dashboardLayoutService';
import messages from './dashboardMessages';
import ResponsiveGridLayout from './ResponsiveGridLayout';
import { withDashboardProvider, injectDashboardContext } from './DashboardProvider';
import ZendeskHelp from '@/shared/components/ZendeskHelp';

const dashboardParamsStorageKey = 'HomeDashboardParams';

class HomeDashboardContainer extends Component {
  state = {
    dashboards: [],
    layouts: {},
    oldLayouts: null, // To store old layout when editing, on cancel we restore this one.
    params: {},
    companies: [],
    breakpoint: 'lg',
  };

  componentDidMount() {
    const dataCache = DataCacheService.getDataCache();
    if (!dataCache) return;
    const dashboards = this.initDashboards();

    const initialCompanyId =
      DataCacheService.getDefaultCompanyForUser()?.id ?? DataCacheService.getDefaultCompany().id;

    layoutService.get().then(storedLayout => {
      const layouts = storedLayout || { ...defaultLayouts };
      const params = userStorage.getItem(dashboardParamsStorageKey) || {
        companyId: initialCompanyId,
      };
      this.setState({
        params,
        dashboards,
        layouts: validateLayouts(layouts, dashboards),
        companies: dataCache.company
          .map(({ id, name }) => ({ id, name }))
          .sort(dynamicSort('name')),
      });
    });
  }

  onBreakpointChange = (breakpoint, cols) => {
    this.setState({
      breakpoint,
      cols,
    });
  };
  onLayoutChange = (layout, layouts) => {
    this.setState({
      layouts,
    });
  };

  initDashboards = () => {
    const { intl } = this.props;

    const dashboards = getEnabledDashboardDefinitions();
    return dashboards.map(item => ({
      ...item,
      title: item.title && item.title.id ? intl.formatMessage(item.title) : item.title, // Translate title
    }));
  };

  reset = () => {
    this.setState(({ dashboards }) => ({
      layouts: validateLayouts({ ...defaultLayouts }, dashboards),
    }));
  };

  renderDashboards = () => {
    const { params, layouts = {}, breakpoint, editMode, dashboards } = this.state;

    const { [breakpoint]: layout = [] } = layouts;

    return layout.map(l => {
      const d = dashboards.find(x => x.i === l.i);
      return (
        <div key={l.i} data-grid={l} className={classnames(`columns-${l.w}`, `rows-${l.h}`)}>
          {d.render(params)}
          {editMode && (
            <span className="remove btn btn-inverse" onClick={this.onHideItem.bind(this, d)}>
              <i className="fa fa-times" />
            </span>
          )}
        </div>
      );
    });
  };

  updateParams = changes => {
    this.setState(
      ({ params }) => ({
        params: { ...params, ...changes },
      }),
      () => {
        const { params } = this.state;
        userStorage.setItem(dashboardParamsStorageKey, params);
      },
    );
  };

  renderCompanyFilter = () => {
    const {
      companies,
      params: { companyId },
    } = this.state;
    if (!companies || companies.length <= 1) return null;

    return (
      <div className="form-group margin10R">
        <label htmlFor="OwnCompanyFilter" className="control-label margin5R">
          <FormattedMessage {...messages.OwnCompany} />
        </label>
        <select
          id="OwnCompanyFilter"
          className="form-control input-sm"
          value={companyId || ''}
          onChange={e => this.updateParams({ companyId: e.target.value })}
        >
          {companies.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
    );
  };
  renderHiddenItemsMenu = () => {
    const { dashboards, layouts = {}, breakpoint } = this.state;
    const { intl } = this.props;
    const layout = layouts[breakpoint] || [];
    const hiddenItems = dashboards
      .filter(x => !layout.some(l => l.i === x.i))
      .sort(dynamicSort('title'));
    if (!hiddenItems || !hiddenItems.length) return null;
    return (
      <DropdownButton
        bsStyle="default"
        bsSize="sm"
        id="add-dashboard-menu-button"
        title={intl.formatMessage(messages.AddDashboard)}
      >
        {hiddenItems.map(item => (
          <MenuItem key={item.i} onSelect={() => this.onShowItem(item)}>
            {item.title}
          </MenuItem>
        ))}
      </DropdownButton>
    );
  };

  onShowItem = item => {
    const { dashboardContext } = this.props;
    dashboardContext.setEditMode(true);

    this.setState(({ layouts, oldLayouts }) => ({
      oldLayouts: dashboardContext.editMode ? oldLayouts : JSON.stringify(layouts),
      layouts: addItemToLayouts(layouts, item),
    }));
  };
  onHideItem = item => {
    this.setState(({ layouts = {} }) => ({
      layouts: removeItemFromLayouts(layouts, item),
    }));
  };

  enableEditMode = () => {
    const { dashboardContext } = this.props;
    this.setState(({ layouts }) => ({ editMode: true, oldLayouts: JSON.stringify(layouts) }));
    dashboardContext.setEditMode(true);
  };
  saveLayout = () => {
    const { layouts } = this.state;
    const { dashboardContext } = this.props;
    return layoutService.save(layouts).then(() => {
      this.setState({ editMode: false, oldLayouts: null });
      dashboardContext.setEditMode(false);
    });
  };
  cancelLayout = () => {
    const { oldLayouts, layouts } = this.state;
    const { dashboardContext } = this.props;
    if (oldLayouts === JSON.stringify(layouts)) {
      // Nothing changed
      this.setState({ editMode: false, oldLayouts: null });
    } else {
      this.setState({ editMode: false, layouts: JSON.parse(oldLayouts) });
    }
    dashboardContext.setEditMode(false);
  };
  renderToolbar = () => {
    const { dashboardContext } = this.props;
    const { editMode } = dashboardContext;
    if (!editMode)
      return (
        <React.Fragment>
          <button
            onClick={() => this.enableEditMode()}
            type="button"
            className="btn btn-default btn-sm margin10L margin10R"
          >
            <FaExpandArrowsAlt className="react-icon" />{' '}
            <FormattedMessage {...messages.EditLayout} />
          </button>
          {this.renderHiddenItemsMenu()}
          <ZendeskHelp article="5434869475090" />
        </React.Fragment>
      );

    return (
      <React.Fragment>
        <Button
          showProgress
          onClick={() => this.saveLayout()}
          btnClassName="btn-inverse  btn-sm margin10L"
        >
          <FormattedMessage {...messages.SaveLayout} />
        </Button>
        <button
          onClick={() => this.cancelLayout()}
          type="button"
          className="btn btn-default btn-sm margin10L margin10R"
        >
          <FormattedMessage {...messages.Cancel} />
        </button>
        {this.renderHiddenItemsMenu()}
        <button
          onClick={() => this.reset()}
          type="button"
          className="btn btn-default btn-sm margin10L "
        >
          <FormattedMessage {...messages.ResetToDefault} />
        </button>
      </React.Fragment>
    );
  };

  render() {
    const { layouts } = this.state;
    const { dashboardContext } = this.props;
    const { editMode } = dashboardContext;

    return (
      <div>
        <div className="form-inline">
          {this.renderCompanyFilter()}
          {this.renderToolbar()}
        </div>
        <ResponsiveGridLayout
          className="layout"
          compactType="vertical"
          verticalCompact
          layouts={layouts}
          isDraggable={editMode}
          isResizable={editMode}
          onLayoutChange={this.onLayoutChange}
          onBreakpointChange={this.onBreakpointChange}
          breakpoints={breakpoints}
          cols={breakpointCols}
          useCSSTransforms={false}
          style={{ zIndex: '0' }}
        >
          {this.renderDashboards()}
        </ResponsiveGridLayout>

        {/* Display current layout size, for debugging & changing default layout: */}
        {/* <div
          style={{
            position: 'fixed',
            top: '80px',
            left: '5px',
            padding: '5px',
            backgroundColor: '#ccc',
            opacity: '0.5',
            color: '#000',
          }}
        >
          {this.state.breakpoint}
        </div> */}
        {editMode ? <div style={{ height: '200px' }} /> : null}
      </div>
    );
  }
}

HomeDashboardContainer.propTypes = {
  intl: intlShape.isRequired,
  dashboardContext: PropTypes.any.isRequired,
};
HomeDashboardContainer.defaultProps = {};

export default injectIntl(withDashboardProvider(injectDashboardContext(HomeDashboardContainer)));
